

// ========================
// STYLED COMPONENTS - HEADER
// ========================

import styled from "styled-components";
import {
    BORDER_GRAY,
    GREE_1,
    GREE_3, LIGHT_GRAY,
    PLANTEI_DARK,
    WHITE
} from "../../utils/colors";
import {
    AppstoreOutlined,
    MenuOutlined, SearchOutlined,
    ShoppingCartOutlined, UserOutlined, WhatsAppOutlined
} from "@ant-design/icons";
import Button from "../../components/data_entry/Button";
import {Badge, Col, Drawer, Dropdown, Input, Row} from "antd";
import {LogoutOutlined} from "@ant-design/icons";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {useEffect, useMemo, useState} from "react";
import {ROUTES} from "../../constants/PathRoutes";
import Select from "../../components/data_entry/Select";
import {useClientAuth} from "../../contexts/ClientAuthContext";
import {useCart, useShop} from "../../contexts/ShopContext";
import {useSiteSettings} from "../../contexts/SiteSettingsContext";

const HeaderTopBar = styled.div`
    padding: 20px 40px;
    background: ${WHITE};
    display: none;

    @media (min-width: 992px) {
        display: block;
    }
`;

const HeaderTopContent = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 24px;
`;

const LogoLink = styled.a`
    display: flex;
    align-items: center;
    gap: 10px;
    white-space: nowrap;
    cursor: pointer;
    text-decoration: none;
    flex-shrink: 0;
`;

const LogoIcon = styled(ShoppingCartOutlined)`
    font-size: 32px;
    color: ${GREE_3};
`;

const LogoText = styled.h1`
    font-size: 28px;
    font-weight: 700;
    color: ${GREE_1};
    margin: 0;
`;

const HeaderSearchWrapper = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    border: 2px solid ${BORDER_GRAY};
    border-radius: 50px;
    overflow: hidden;

    .ant-input {
        border: none;
        box-shadow: none;
        padding: 10px 20px;
        font-size: 15px;
    }

    .ant-select {
        min-width: 160px;
        border-left: 1px solid ${BORDER_GRAY};
    }

    .ant-select .ant-select-selector {
        border: none !important;
        box-shadow: none !important;
        padding: 6px 12px;
    }
`;

const HeaderSearchBtn = styled(Button)`
    height: 48px;
    width: 60px;
    border-radius: 0 50px 50px 0 !important;
    border: none;
    background: ${GREE_1} !important;
    color: ${WHITE} !important;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: ${GREE_3} !important;
    }
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
`;

const HeaderIconBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid ${BORDER_GRAY};
    background: ${WHITE};
    color: ${PLANTEI_DARK};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s;
    font-size: 18px;

    &:hover {
        background: ${GREE_1};
        color: ${WHITE};
        border-color: ${GREE_1};
    }
`;

// Navbar
const NavBarWrapper = styled.div`
    background: ${GREE_1};
    position: sticky;
    top: 0;
    z-index: 100;
`;

const NavBarContent = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 40px;
    display: flex;
    align-items: center;

    @media (max-width: 991px) {
        padding: 0 16px;
    }
`;

const CategoriesToggle = styled.div`
    position: relative;
    width: 250px;
    flex-shrink: 0;
    display: none;

    @media (min-width: 992px) {
        display: block;
    }
`;

const CategoriesBtn = styled.button`
    background: transparent;
    border: none;
    color: ${WHITE};
    font-size: 16px;
    font-weight: 600;
    padding: 16px 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;

    &:hover {
        opacity: 0.9;
    }
`;

const CategoriesDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${WHITE};
    border-radius: 0 0 8px 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    z-index: 200;
    overflow: hidden;
    display: ${props => props.open ? 'block' : 'none'};
`;

const CatDropdownItem = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 10px 16px;
    border-bottom: 1px solid ${LIGHT_GRAY};
    cursor: pointer;
    transition: all 0.3s;
    font-size: 14px;
    color: ${PLANTEI_DARK};

    &:hover {
        background: ${GREE_1};
        color: ${WHITE};
    }

    &:last-child {
        border-bottom: none;
    }

    span {
        color: inherit;
    }
`;

const NavLinks = styled.div`
    display: none;
    flex: 1;

    @media (min-width: 992px) {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0;
    }
`;

const NavLinkItem = styled.a`
    color: ${WHITE};
    font-size: 15px;
    font-weight: 500;
    padding: 18px 16px;
    cursor: pointer;
    transition: color 0.3s;
    text-decoration: none;
    white-space: nowrap;

    &:hover, &.active {
        color: rgba(255, 255, 255, 0.8);
    }
`;

const NavPhoneBtn = styled(Button)`
    border-radius: 50px !important;
    background: ${GREE_3} !important;
    border-color: ${GREE_3} !important;
    color: ${WHITE} !important;
    font-weight: 500;
    height: 40px;
    margin-left: 16px;

    &:hover {
        background: ${GREE_1} !important;
        border-color: ${GREE_1} !important;
    }
`;

// Mobile header
const MobileHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    width: 100%;

    @media (min-width: 992px) {
        display: none;
    }
`;

const MobileLogo = styled.h1`
    font-size: 22px;
    font-weight: 700;
    color: ${WHITE};
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MobileMenuBtn = styled.button`
    background: transparent;
    border: none;
    color: ${WHITE};
    font-size: 22px;
    cursor: pointer;
    padding: 4px 8px;
`;

const DrawerCatItem = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid ${LIGHT_GRAY};
    cursor: pointer;
    font-size: 14px;
    color: ${PLANTEI_DARK};

    &:hover {
        color: ${GREE_1};
    }
`;

const DrawerNavItem = styled.a`
    display: block;
    padding: 12px 0;
    font-size: 15px;
    font-weight: 500;
    color: ${PLANTEI_DARK};
    border-bottom: 1px solid ${LIGHT_GRAY};
    cursor: pointer;

    &:hover {
        color: ${GREE_1};
    }
`;

// ========================
// STYLED COMPONENTS - SHOP
// ========================

const PageHeader = styled.div`
    background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
        url('https://via.placeholder.com/1920x400/1B7573/ffffff?text=CEBRAPCAM+Loja');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    padding: 80px 0;
    text-align: center;
`;

const PageHeaderTitle = styled.h1`
    color: ${WHITE};
    font-size: 36px;
    font-weight: 700;
    margin: 0;

    @media (max-width: 768px) {
        font-size: 28px;
    }
`;

const ShopContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 24px;

    @media (max-width: 768px) {
        padding: 24px 16px;
    }
`;







export default function ShopLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { cartCount } = useCart();
    const { categories } = useShop();
    const { logout } = useClientAuth();
    const { settings } = useSiteSettings();

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [location.pathname]);

    const [headerSearch, setHeaderSearch] = useState('');
    const [headerCategory, setHeaderCategory] = useState('all');
    const [showCategories, setShowCategories] = useState(false);
    const [mobileDrawer, setMobileDrawer] = useState(false);

    const handleHeaderSearch = () => {
        const params = new URLSearchParams();
        if (headerSearch.trim()) params.set('search', headerSearch.trim());
        if (headerCategory !== 'all') params.set('category', headerCategory);
        navigate(`/area-do-associado/loja/produtos?${params.toString()}`);
        setHeaderSearch('');
        setHeaderCategory('all');
    };

    const handleNavCategoryClick = (catId) => {
        setShowCategories(false);
        setMobileDrawer(false);
        navigate(`/area-do-associado/loja/produtos?category=${catId}`);
    };

    return (
        <div>
            {/* ====== HEADER - Topbar (Desktop) ====== */}
            <HeaderTopBar>
                <HeaderTopContent>
                    {/* Logo */}
                    <LogoLink onClick={() => navigate('/area-do-associado/loja/produtos')}>
                        <LogoIcon />
                        <LogoText>CEBRAPCAM</LogoText>
                    </LogoLink>

                    {/* Search */}
                    <HeaderSearchWrapper>
                        <Input
                            placeholder="O que você está procurando?"
                            value={headerSearch}
                            onChange={(e) => setHeaderSearch(e.target.value)}
                            onPressEnter={handleHeaderSearch}
                            variant="borderless"
                            style={{ flex: 1 }}
                        />
                        <Select
                            value={headerCategory}
                            onChange={setHeaderCategory}
                            variant="borderless"
                            options={[
                                { value: 'all', label: 'Todas categorias' },
                                ...categories.map(c => ({
                                    value: String(c.id),
                                    label: c.name
                                }))
                            ]}
                        />
                        <HeaderSearchBtn onClick={handleHeaderSearch}>
                            <SearchOutlined />
                        </HeaderSearchBtn>
                    </HeaderSearchWrapper>

                    {/* Actions */}
                    <HeaderActions>
                        <Badge count={cartCount} size="small" style={{ backgroundColor: GREE_1 }}>
                            <HeaderIconBtn onClick={() => navigate(ROUTES.LOJA_CARRINHO)}>
                                <ShoppingCartOutlined />
                            </HeaderIconBtn>
                        </Badge>
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'profile',
                                        label: 'Meu Perfil',
                                        icon: <UserOutlined />,
                                        onClick: () => navigate('/area-do-associado/loja/perfil'),
                                    },
                                    {
                                        key: 'logout',
                                        label: 'Sair',
                                        icon: <LogoutOutlined />,
                                        danger: true,
                                        onClick: logout,
                                    },
                                ],
                            }}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <HeaderIconBtn>
                                <UserOutlined />
                            </HeaderIconBtn>
                        </Dropdown>
                    </HeaderActions>
                </HeaderTopContent>
            </HeaderTopBar>

            {/* ====== HEADER - Navbar ====== */}
            <NavBarWrapper>
                <NavBarContent>
                    {/* Categories Toggle (Desktop) */}
                    <CategoriesToggle>
                        <CategoriesBtn onClick={() => setShowCategories(!showCategories)}>
                            <MenuOutlined />
                            Categorias
                        </CategoriesBtn>
                        <CategoriesDropdown open={showCategories}>
                            {categories.map(cat => (
                                <CatDropdownItem
                                    key={cat.id}
                                    onClick={() => handleNavCategoryClick(cat.id)}
                                >
                                    <span>{cat.name}</span>
                                </CatDropdownItem>
                            ))}
                        </CategoriesDropdown>
                    </CategoriesToggle>

                    {/* Nav Links (Desktop) */}
                    <NavLinks>
                        <NavLinkItem className="active" onClick={() => navigate('/area-do-associado/loja/produtos')}>
                            Início
                        </NavLinkItem>
                        {settings?.whatsapp && (
                            <NavPhoneBtn
                                icon={<WhatsAppOutlined />}
                                onClick={() => window.open(`https://wa.me/${settings.whatsapp}`, '_blank')}
                            >
                                Fale conosco
                            </NavPhoneBtn>
                        )}
                    </NavLinks>

                    {/* Mobile Header */}
                    <MobileHeader>
                        <MobileLogo>
                            <ShoppingCartOutlined />
                            CEBRAPCAM
                        </MobileLogo>
                        <MobileMenuBtn onClick={() => setMobileDrawer(true)}>
                            <MenuOutlined />
                        </MobileMenuBtn>
                    </MobileHeader>
                </NavBarContent>
            </NavBarWrapper>

            {/* Mobile Drawer */}
            <Drawer
                title="Menu"
                placement="right"
                onClose={() => setMobileDrawer(false)}
                open={mobileDrawer}
            >
                <DrawerNavItem onClick={() => { navigate('/area-do-associado/loja/produtos'); setMobileDrawer(false); }}>
                    Início
                </DrawerNavItem>
                <DrawerNavItem onClick={() => { navigate('/'); setMobileDrawer(false); }}>
                    Contato
                </DrawerNavItem>
                <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600, fontSize: 16 }}>
                    Categorias
                </div>
                {categories.map(cat => (
                    <DrawerCatItem key={cat.id} onClick={() => handleNavCategoryClick(cat.id)}>
                        <span>{cat.name}</span>
                    </DrawerCatItem>
                ))}
            </Drawer>

            {/* Page Header */}
            <PageHeader>
                <PageHeaderTitle>Loja do Associado</PageHeaderTitle>
            </PageHeader>

            {/* Shop Content */}
            <ShopContainer>
                <Outlet/>
            </ShopContainer>
        </div>
    );
}